import { useRouterState } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Progress } from "design-system/atoms/Progress"
import { useSidebar } from "design-system/organisms/Sidebar"
import { AnimatePresence, motion } from "framer-motion"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { Loading } from "modules/shared/components/Loading"
import { useSubscriptionData } from "modules/subscription/hooks/useSubscriptionData"
import { canSeeSubscription } from "modules/subscription/lib/canSeeSubscription"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { SidebarUsageContent } from "./SidebarUsageContent"

const MotionButton = motion.create(Button)

const PERCENTAGE_HEIGHT = 48
const REGULAR_HEIGHT = 82
const REGULAR_HEIGHT_WITH_ADDON = 116

export function SidebarUsage() {
  const { isMobile, state } = useSidebar()
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [expandedHeight, setExpandedHeight] = useState<number>(0)
  const expandedRef = useRef<HTMLDivElement>(null)
  const { location } = useRouterState()
  const {
    isPending,
    totalSubscriptionCredits,
    usedSubscriptionCredits,
    subscriptionIsError,
    hasAddon,
    showAddonOnPreview,
    totalAddonCredits,
    usedAddonCredits,
    addonIsError,
  } = useSubscriptionData()
  const { data: employee } = useAuthEmployeeQuery()

  const showSidebarContent = state === "expanded" || isMobile

  useEffect(() => {
    if (showSidebarContent && expandedRef.current) {
      const height = expandedRef.current.scrollHeight
      setExpandedHeight(height)
    }
  }, [showSidebarContent])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  if (employee && !canSeeSubscription(employee)) {
    return null
  }

  if (isPending) {
    return <Loading />
  }

  const regularHeight = showAddonOnPreview ? REGULAR_HEIGHT_WITH_ADDON : REGULAR_HEIGHT

  return (
    <div className="px-2 flex flex-col gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <MotionButton
            className="w-full h-auto bg-primary/5 hover:bg-primary/10 data-[state=open]:bg-primary/10 overflow-hidden p-0 rounded-lg"
            variant="sidebar"
            style={{
              position: "relative",
            }}
            layout
            animate={{
              height: showSidebarContent ? expandedHeight || regularHeight : PERCENTAGE_HEIGHT,
            }}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {showSidebarContent ? (
                <motion.div
                  key="expanded"
                  ref={expandedRef}
                  className="flex flex-col gap-2 items-start w-full p-3"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <span className="text-base font-medium">{t("subscription.usage.title")}</span>
                  <div className="flex flex-col gap-1 items-center w-full">
                    <Progress
                      value={Math.max(usedSubscriptionCredits, 0)}
                      max={totalSubscriptionCredits}
                      color={subscriptionIsError ? "destructive" : "primary"}
                    />
                    <div className="flex flex-row justify-between w-full text-xs items-center font-mono text-muted-foreground">
                      <span>{usedSubscriptionCredits}</span>
                      <span className="text-foreground">{totalSubscriptionCredits}</span>
                    </div>
                  </div>
                  {showAddonOnPreview && (
                    <div className="flex flex-col gap-1 items-center w-full">
                      <Progress
                        value={usedAddonCredits}
                        max={totalAddonCredits}
                        color={addonIsError ? "destructive" : "gradient"}
                      />
                      <div className="flex flex-row justify-between w-full text-xs items-center font-mono text-muted-foreground">
                        <span>{usedAddonCredits}</span>
                        <span className="text-foreground">{totalAddonCredits}</span>
                      </div>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="collapsed"
                  className="flex h-12 w-full items-center justify-center relative"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <Progress
                    value={usedSubscriptionCredits}
                    max={totalSubscriptionCredits}
                    variant="circle"
                    size={40}
                    width={3}
                    className="h-8 w-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    color={subscriptionIsError ? "destructive" : "primary"}
                  />
                  {hasAddon && (
                    <Progress
                      value={usedAddonCredits}
                      max={totalAddonCredits}
                      variant="circle"
                      color={addonIsError ? "destructive" : "gradient"}
                      size={30}
                      width={3}
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </MotionButton>
        </PopoverTrigger>
        <PopoverContent
          side={isMobile ? "bottom" : "right"}
          align={isMobile ? "center" : "end"}
          sideOffset={isMobile ? 0 : 10}
          className="max-h-none! overflow-visible!"
        >
          <SidebarUsageContent />
        </PopoverContent>
      </Popover>
    </div>
  )
}
